/**
 * SelectTextPrint - Figma-matching print renderer for select-text quizzes
 * Layout: PrintQuizTitle → image → problem text with inline selector boxes
 * Selector: h-40px, px-16px py-8px, options separated by "/" divider (#ccc)
 */

import type { ReactNode } from 'react'
import { cn } from '../../../lib/utils'
import { KatexText } from '../shared'
import type { SelectTextQuizData } from '../../../types/legacyQuiz'
import { formatProblem } from '../../../utils/quiz-parser'
import { PRINT_CORRECT_ANSWER_CLASS, PrintQuizTitle, PrintQuizImage, getStyles, getImageUrl, getPrintScale } from './print-common'
import { parseSelectTextOptionModels } from '../hooks'

interface PrintQuizProps {
  quizId: string | number
  quiz: SelectTextQuizData
  number: number
  showCorrectAnswers?: boolean
  correctAnswers?: string[]
  compact?: boolean
}

export function SelectTextPrint({
  quizId,
  quiz,
  number,
  showCorrectAnswers = false,
  correctAnswers,
  compact,
}: PrintQuizProps) {
  const s = getStyles(compact)
  const imageUrl = getImageUrl(quiz.image, quiz.imagePrint)
  const printScale = getPrintScale(quiz.image, quiz.imagePrint)

  // Render problem with inline option selector boxes
  const renderedProblem: ReactNode[] = formatProblem(
    quiz.problem,
    `${quizId}-print-problem`,
    (index: number, content: string) => {
      const options = parseSelectTextOptionModels(content).filter(option => option.isSelectable)
      const correctIdx = correctAnswers?.[index] != null
        ? parseInt(correctAnswers[index])
        : 0

      return (
        <span
          key={`select-print-${quizId}-${index}`}
          className={cn(
            s.inputBoxSm,
            'inline-flex items-center align-middle',
            compact ? 'px-[10px] py-[8px] mx-[4px] my-[4px]' : 'px-[16px] py-[8px] mx-[12px]'
          )}
        >
          {options.map((option, visibleIndex) => {
            const isCorrect = option.rawIndex === correctIdx
            return (
              <span key={option.rawIndex} className="flex items-center">
                {visibleIndex > 0 && (
                  <span className="text-[#ccc] mx-[12px]">/</span>
                )}
                <span
                  className={cn(
                    compact ? 'text-[15px] tracking-[-0.09px]' : 'text-[16px] tracking-[-0.096px]',
                    showCorrectAnswers && isCorrect
                      ? PRINT_CORRECT_ANSWER_CLASS
                      : 'text-black'
                  )}
                >
                  <KatexText text={option.label} />
                </span>
              </span>
            )
          })}
        </span>
      )
    },
    quiz.additionalImages ?? undefined
  )

  return (
    <div className={cn(s.container, s.quizSection)}>
      {/* Title */}
      <PrintQuizTitle number={number} compact={compact}>
        <KatexText text={quiz.title} />
      </PrintQuizTitle>

      {/* Image */}
      {imageUrl && <PrintQuizImage src={imageUrl} printScale={printScale} />}

      {/* Problem with inline selectors — needs taller line-height to fit inline boxes */}
      <div className={s.problemArea}>
        <div className={cn(
          s.problemLine,
          compact
            ? 'text-[16px] text-black tracking-[-0.096px] font-normal leading-[41px]'
            : s.problemText
        )}>
          {renderedProblem}
        </div>
      </div>
    </div>
  )
}
